import React, { PropsWithChildren, useState } from 'react';
import DialogModal from '@/Components/DialogModal';
import Button from '@/Components/Button';
import CopyUrlButton from '@/Components/CopyLinkButton';
import IconLink from '@/svgs/link.svg?react';
import useRoute from '@/Hooks/useRoute';
import { router } from '@inertiajs/react';
import useTypedPage from '@/Hooks/useTypedPage';

interface Props {
  title?: string;
  file: App.Data.FileData;
}

export default function ShareDialog({
  title = 'Share this Snippet',
  children,
  file,
}: PropsWithChildren<Props>) {
  const [open, setOpen] = useState<boolean>(false);
  const [isShared, setIsShared] = useState<boolean>(file.public);
  const route = useRoute();
  const { short_url: shortUrl } = useTypedPage<{
    short_url?: string;
  }>().props;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    router.put(
      route('files.update', file.id),
      { public: !file.public },
      {
        preserveState: true,
        onSuccess: data => {
          // Nothing happened, assume an error.
          if (data.props.file.public === isShared) {
            setOpen(false);
          } else {
            setIsShared(!isShared);
          }
        },
        onError: () => setOpen(false),
      },
    );
  }

  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>

      <DialogModal isOpen={open} onClose={() => setOpen(false)}>
        <DialogModal.Content title={title} onClose={() => setOpen(false)}>
          <form
            id="createPackageFile"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="flex items-center justify-between rounded-xl bg-brand-75 p-4 dark:bg-brand-dark-20">
              <div>
                <legend className="text-lg font-medium dark:text-brand-light-100">
                  Make Snippet Public
                </legend>
                <p className="text-brand-dark-100/40 dark:text-brand-light-100">
                  Anyone on the internet with the link can view.
                </p>
              </div>
              <Button
                className="bg-brand dark:bg-brand-dark dark:text-brand-dark-100"
                type="submit"
              >
                {isShared ? 'Unpublish' : 'Publish'}
              </Button>
            </div>
          </form>
        </DialogModal.Content>
        <div className="border-brand-gray-4 flex items-center justify-between border-t px-6 py-4 dark:border-brand-dark-20">
          <CopyUrlButton
            url={shortUrl}
            className="flex items-center gap-2 text-brand-dark-100/40 dark:text-brand-light-100"
          >
            <IconLink className="h-5 w-5 text-base" />
            Copy URL
          </CopyUrlButton>
        </div>
      </DialogModal>
    </>
  );
}
